import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { VideoPlayerModalComponent } from './shared/components/video-player-modal';
import { ImagePlayerModalComponent } from './shared/components/image-player-modal';
import { PdfViewerModalComponent } from './shared/components/pdf-viewer-modal';
import { TextViewerModalComponent } from './shared/components/text-viewer-modal';
import { WordViewerModalComponent } from './shared/components/word-viewer-modal';
import { TranslateService } from '@ngx-translate/core';
import { IdleTimeoutService } from './core/services/idle-timeout.service';
import { AuthService } from './core/services/auth.service';
import { PreferencesService } from './core/services/preferences.service';
import { NotificationInformerComponent } from './shared/components/notification-informer/notification-informer.component';
import { MobileAlertComponent } from './shared/components/mobile-alert/mobile-alert.component';
import { MobileAlertService } from './core/services/mobile-alert.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    VideoPlayerModalComponent,
    ImagePlayerModalComponent,
    PdfViewerModalComponent,
    TextViewerModalComponent,
    WordViewerModalComponent,
    NotificationInformerComponent,
    MobileAlertComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit {
  title = 'frontend-ehpad';

  constructor(
    private idleTimeoutService: IdleTimeoutService,
    private translateService: TranslateService,
    private authService: AuthService,
    private preferencesService: PreferencesService,
    // Force l’init du bridge Swal → modale mobile dès le démarrage
    _mobileAlert: MobileAlertService
  ) {
    // Initialiser la surveillance d'inactivité globale
    this.idleTimeoutService.init();
  }

  ngOnInit(): void {
    // Configurer les langues disponibles
    this.translateService.addLangs(['fr', 'en']);
    // Définir la langue par défaut
    this.translateService.setDefaultLang('fr');
    
    // Charger d'abord la langue par défaut pour que les traductions soient disponibles
    const savedLang = localStorage.getItem('preferredLanguage') || 'fr';
    
    // Forcer le rechargement en ajoutant un timestamp pour éviter le cache
    this.translateService.reloadLang(savedLang).subscribe({
      next: () => {
        this.translateService.use(savedLang).subscribe({
          next: () => {
            // Une fois la langue chargée, essayer de charger depuis les préférences utilisateur
            this.loadUserLanguage();
          },
          error: () => {
            // En cas d'erreur, utiliser la langue par défaut
            this.translateService.use('fr');
          }
        });
      },
      error: () => {
        // En cas d'erreur, charger normalement
        this.translateService.use(savedLang).subscribe({
          next: () => {
            this.loadUserLanguage();
          },
          error: () => {
            this.translateService.use('fr');
          }
        });
      }
    });
  }

  /**
   * Charge la langue depuis les préférences utilisateur ou localStorage
   */
  private loadUserLanguage(): void {
    const currentUser = this.authService.getCurrentUser();

    // Ne pas appeler l'API avec un user stale (sessionStorage) sans access token —
    // typique au retour SSO/Social callback : 401 → toast « Session expirée » pendant l'échange ticket.
    if (currentUser?.id && this.authService.isLoggedIn()) {
      // Charger les préférences depuis l'API
      this.preferencesService.getUserPreferences(currentUser.id).subscribe({
        next: (response) => {
          if (response.success && response.data?.language) {
            const userLanguage = response.data.language;
            this.translateService.use(userLanguage).subscribe();
            localStorage.setItem('preferredLanguage', userLanguage);
          } else {
            // Fallback sur localStorage si pas de préférences
            const savedLang = localStorage.getItem('preferredLanguage') || 'fr';
            this.translateService.use(savedLang);
          }
        },
        error: () => {
          // En cas d'erreur, utiliser localStorage
          const savedLang = localStorage.getItem('preferredLanguage') || 'fr';
          this.translateService.use(savedLang);
        }
      });
    } else {
      // Pas d'utilisateur connecté, utiliser localStorage
      const savedLang = localStorage.getItem('preferredLanguage') || 'fr';
      this.translateService.use(savedLang);
    }
  }
}
